import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AgencyPaymentMethodModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/agencyPaymentMethod/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/agencyPaymentMethod/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/agencyPaymentMethod/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/agencyPaymentMethod/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/agencyPaymentMethod/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/agencyPaymentMethod/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/agencyPaymentMethod/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/agencyPaymentMethod/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/agencyPaymentMethod/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/agencyPaymentMethod/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/agencyPaymentMethod/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/agencyPaymentMethod/DELETE_FAILURE'

export type AgencyPaymentMethodModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AgencyPaymentMethodModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAgencyPaymentMethods(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().agencyPaymentMethods.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/agency_payment_methods.json', options)
      .then(({ data }: { data: AgencyPaymentMethodModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PAYMENT_METHOD_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadAgencyPaymentMethod(agencyPaymentMethodId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.agencyPaymentMethods.loadedForKeys]
    const loadedIds = [...state.agencyPaymentMethods.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (agencyPaymentMethodId && !loadedIds.includes(agencyPaymentMethodId)){
      loadedIds.push(agencyPaymentMethodId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/agency_payment_methods/${agencyPaymentMethodId}.json`, options)
      .then(({ data }: { data: AgencyPaymentMethodModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PAYMENT_METHOD)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createAgencyPaymentMethod(agencyPaymentMethod: Partial<AgencyPaymentMethodModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency_payment_method: agencyPaymentMethod,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/agency_payment_methods.json', options, config)
      .then(({ data }: { data: AgencyPaymentMethodModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PAYMENT_METHOD)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateAgencyPaymentMethod(
  agencyPaymentMethod: UpdateParams<AgencyPaymentMethodModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      agency_payment_method: agencyPaymentMethod,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agency_payment_methods/${agencyPaymentMethod.id}.json`, options, config)
      .then(({ data }: { data: AgencyPaymentMethodModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PAYMENT_METHOD)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAgencyPaymentMethod(agencyPaymentMethod: DeleteParams<AgencyPaymentMethodModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/agency_payment_methods/${agencyPaymentMethod.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PAYMENT_METHOD)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AgencyPaymentMethodModuleState = initialState,
  action: Action = { type: '' },
): AgencyPaymentMethodModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
